
import { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  color: string;
}

const StarsBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('StarsBackground: Canvas ref is null');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('StarsBackground: Could not get 2D context');
        return;
      }
    
    let animationFrameId: number;
    let stars: Star[] = [];
    let isPaused = document.hidden;
    let scrollTimeout: number;

    const onVisibilityChange = () => {
      isPaused = document.hidden;
    };

    const onScroll = () => {
      isPaused = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        isPaused = false;
      }, 120);
    };
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };
    
    const initStars = () => {
      stars = [];
      const area = canvas.width * canvas.height;
      const base = Math.floor(area / 2000);
      const maxStars = 900;
      const isMobile = window.innerWidth < 768;
      const numStars = Math.min(base, maxStars) * (isMobile ? 0.6 : 1);
      
      
      const colors = [
        '#ffffff', // White
        '#d4d4ff', // Light blue-white
        '#9b87f5', // Purple
        '#0EA5E9', // Blue
        '#ffd700', // Gold
        '#ff8a80', // Pink
      ];
      
      for (let i = 0; i < numStars; i++) {
        const sizeCategory = Math.random();
        let size, opacity, speed;
        
        if (sizeCategory < 0.7) {
          // Tiny stars - most common
          size = 0.5 + Math.random() * 0.8;
          opacity = 0.3 + Math.random() * 0.4;
          speed = Math.random() * 0.05;
        } else if (sizeCategory < 0.95) {
          // Medium stars
          size = 1.2 + Math.random() * 1.5;
          opacity = 0.5 + Math.random() * 0.4;
          speed = Math.random() * 0.08;
        } else {
          // Large prominent stars
          size = 2 + Math.random() * 2;
          opacity = 0.7 + Math.random() * 0.3;
          speed = Math.random() * 0.1;
        }
        
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size,
          opacity,
          speed,
          color: colors[Math.floor(Math.random() * colors.length)]
        });
      }
    };
    
    const drawStar = (ctx: CanvasRenderingContext2D, star: Star) => {
      ctx.save();
      ctx.globalAlpha = star.opacity;
      
      if (star.size < 1) {
        // Tiny stars - just points
        ctx.fillStyle = star.color;
        ctx.fillRect(Math.floor(star.x), Math.floor(star.y), 1, 1);
      } else if (star.size < 2) {
        // Medium stars - small circles with subtle glow
        ctx.fillStyle = star.color;
        ctx.shadowBlur = 1;
        ctx.shadowColor = star.color;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * 0.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Large stars - circles with pronounced glow
        ctx.fillStyle = star.color;
        ctx.shadowBlur = 3;
        ctx.shadowColor = star.color;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();
    };
    
    let lastFrameTime = 0;
    const targetFPS = 24;
    const frameInterval = 1000 / targetFPS;
    
    const animate = (currentTime: number) => {
      animationFrameId = requestAnimationFrame(animate);
      
      if (isPaused) {
        lastFrameTime = currentTime;
        return;
      }
      
      // Throttle to 24fps for better performance
      const elapsed = currentTime - lastFrameTime;
      if (elapsed < frameInterval) return;
      
      lastFrameTime = currentTime - (elapsed % frameInterval);
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const time = currentTime * 0.001;
      
      stars.forEach(star => {
        drawStar(ctx, star);
        
        // Very slow drift
        star.y += star.speed;
        
        // Subtle pulsing based on star size
        const pulseSpeed = star.size > 2 ? 0.003 : star.size > 1 ? 0.002 : 0.001;
        const baseOpacity = star.size > 2 ? 0.7 : star.size > 1 ? 0.5 : 0.3;
        const pulseAmount = star.size > 2 ? 0.3 : star.size > 1 ? 0.2 : 0.15;
        
        star.opacity = baseOpacity + Math.sin(time * pulseSpeed + star.x * 0.01) * pulseAmount;
        
        // Reset position when off screen
        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });
    };
    
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    animate(0);
    
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
    } catch (error) {
      console.error('StarsBackground: Error during initialization', error);
    }
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ 
        opacity: 0.95,
        zIndex: 2,
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        contain: 'strict'
      }}
    />
  );
};

export default StarsBackground;
